'use client'

import { getI18n, I18nDictionary, LangCode, useI18nValue } from '@/components/i18n/lang-context'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils/utils'
import { Partner } from '@/types/partner'
import Image from 'next/image'
import Link from 'next/link'

const I18N_CARD: I18nDictionary = {
  visitSite: {
    en: 'Visit website',
    fr: 'Visiter le site'
  },
  visitSiteTitle: {
    en: 'Visit {partner} website',
    fr: 'Visiter le site de {partner}'
  },
  sendEmailTitle: {
    en: 'Send an email to {partner}',
    fr: 'Envoyer un email à {partner}'
  },
  callTitle: {
    en: 'Call {partner}',
    fr: 'Appeler {partner}'
  },
  logoAlt: {
    en: '{partner} logo',
    fr: 'Logo {partner}'
  },
  propertyAlt: {
    en: '{partner} property {index}',
    fr: 'Propriété {partner} {index}'
  },
  showAll: {
    en: 'Show others',
    fr: 'Afficher tout'
  },
  showAllAlt: {
    en: 'Other partner photo {index}',
    fr: 'Photo autre partenaire {index}'
  }
}

const getImage = (partners: Partner[], index: number, i18n: I18nDictionary) => {
  const images: string[] = partners[index].backgroundImages as string[]
  const image = images[index % 2 ? 1 : 2]
  return (
  <div key={index} className='relative overflow-hidden col-span-1 row-span-1 rounded-t-xl'>
    <Image
      src={image}
      alt={i18n.propertyAlt.replace('{index}', index)}
      fill
      className='object-cover transition-transform duration-300 group-hover:scale-110 blur-sm'
      sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
    />
  </div>)
}
export function showMore(otherPartners: Partner[], langCode: LangCode, embedded?: boolean, small?: boolean) {
  const i18n = getI18n(I18N_CARD, langCode)
  return (
  <div className='relative'>
    <Card
      variant='shadow'
      className={cn(
        'group relative ', embedded ? 'h-[270px]' : 'h-[400px]',
        ' overflow-hidden rounded-2xl border-0 bg-gradient-to-br from-blue-50 to-indigo-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl',
        embedded ? '' : 'mt-[-80px]' // Half of logo height to position it on edge
      )}
    >
      {/* Background images in collage style */}
      <div className='absolute inset-0 grid grid-cols-2 gap-1 p-2'>
        <>{getImage(otherPartners, 1, i18n)}{getImage(otherPartners, 2, i18n)}{getImage(otherPartners, 3, i18n)}{getImage(otherPartners, 4, i18n)}</>
      </div>
      {/* Radial gradient overlay for better text readability */}
      <>
        <div className='bg-radial-gradient absolute inset-0' />
        <style jsx>{`.bg-radial-gradient {
            background: radial-gradient(circle at center, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.45) 40%, rgba(0, 0, 0, 0.2) 70%, rgba(0, 0, 0, 0.01) 100%);
          }
        `}</style>
      </>
      {/* Content */}
      <div className={cn('relative flex h-full flex-col items-center gap-0 p-6 ', embedded ? 'pt-4' : 'pt-20', ' text-white')}>
        <div className={cn('h-1/2 ', embedded ? 'pt-4' : 'pt-4')}>
          <div className='mt-20 rounded-full border border-white/30 bg-white/20 px-6 py-2 text-lg font-medium backdrop-blur-sm transition-all duration-300 hover:bg-white/30'>
            {i18n.showAll}
          </div>
        </div>
        <div className=' inset-0 grid grid-cols-2'>
          <div></div>
          <div className={cn(embedded ? ('w-36' + (small ? '' : ' sm:w-[300px]')): 'w-44 xl:w-52', 'text-center text-3xl mt-12')}>
            {'+'+(otherPartners.length)}
          </div>
        </div>
      </div>
    </Card>
  </div>)
}

export function PartnerCard({ partner, className, langCode, highlighted = false, embedded = false }:
  {partner: Partner, className?: string, langCode: LangCode, highlighted?: boolean, embedded?: boolean}) {
  const i18n = getI18n(I18N_CARD, langCode)
  const i18nValue = useI18nValue()

  const description = !partner.description ? undefined : i18nValue(partner.description, langCode)
  const title = !partner.title ? undefined : i18nValue(partner.title, langCode)
  const location = i18nValue(partner.location, langCode)
  const textColor = partner.backgroundImages ? 'text-white' : 'text-gray-700'

  // Render for real estate partners (original design)
  return (
    <div className={cn('relative', className)}>
      {/* Logo positioned on top edge */}
      {!embedded && partner.logo &&
        <div className='relative z-10 flex justify-center'>
          <Link href={i18nValue(partner.website, langCode)}
                target='_blank' rel='noopener noreferrer'
                title={i18n.visitSiteTitle.replace('{partner}', partner.name)}
                className='transition-transform duration-300 hover:scale-105'>
            <div className='relative overflow-hidden rounded-xl bg-white shadow-lg ring-4 ring-white/20 transition-all duration-300 hover:ring-white/40'
                 style={{ width: `${partner.logoW}px`, height: `${partner.logoH}px` }}>
              <Image src={partner.logo!}
                     alt={i18n.logoAlt.replace('{partner}', partner.name)}
                     fill className='object-contain p-2' sizes='800px'/>
            </div>
          </Link>
        </div>
      }

      <Card
        variant='shadow'
        className={cn(
          'group relative ', embedded ? 'h-[270px]' : 'h-[400px]', highlighted ? ' sm:h-[500px]' : '',
          ' overflow-hidden rounded-2xl border-0 bg-gradient-to-br from-blue-50 to-indigo-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl',
          embedded ? '' : 'mt-[-80px]' // Half of logo height to position it on edge
        )}
      >
        {/* Background images in collage style */}
        {partner.backgroundImages && (
          <div className='absolute inset-0 grid grid-cols-2 gap-1 p-2'>
            {partner.backgroundImages.map((image: string, index: number) => (
              <div
                key={index}
                className={cn(
                  'relative overflow-hidden',
                  index === 0 && 'col-span-2 row-span-1 rounded-t-xl',
                  index === 1 && 'col-span-1 row-span-1 rounded-bl-xl',
                  index === 2 && 'col-span-1 row-span-1 rounded-br-xl'
                )}
              >
                <Image
                  src={image}
                  alt={i18n.propertyAlt.replace('{partner}', partner.name).replace('{index}', String(index + 1))}
                  fill
                  className='object-cover transition-transform duration-300 group-hover:scale-110'
                  sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                />
              </div>
            ))}
          </div>
        )}

        {/* Radial gradient overlay for better text readability */}
        {partner.backgroundImages && (<>
          <div className='bg-radial-gradient absolute inset-0' />
          <style jsx>{`.bg-radial-gradient {
              background: radial-gradient(circle at center, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.45) 40%, rgba(0, 0, 0, 0.2) 70%, rgba(0, 0, 0, 0.01) 100%);
            }
          `}</style>
        </>)}

        {/* Content */}
        <div className={cn('relative flex h-full flex-col items-center gap-0 p-6 ', embedded ? 'pt-8' : 'pt-20', highlighted ? ' sm:pt-24' : '', textColor)}>
          <div className='h-1/2 pt-4'>
            {(!embedded && !description) && <p className='text-center text-sm'>&nbsp;</p>}
            {/* Agency name */}
            <div className={cn('mb-2 text-center text-2xl', highlighted ? ' sm:text-4xl' : '', ' font-bold')}>{partner.name}</div>

            {/* Title if available */}
            {title && (
              <p className='mb-2 text-center text-lg font-medium'>{title}</p>
            )}
            {/* Description if available */}
            <p className={cn('mb-2 text-center text-sm', highlighted ? ' sm:text-lg' : '', ' font-bold')}>
              {description ? description : ''}
            </p>
          </div>

          {/* Contact information */}
          <div className={cn(highlighted ? 'sm:pt-4 ' : '', 'space-y-2 text-center')}>
            {!embedded && !partner.email && <span className={cn('text-sm', highlighted ? ' sm:text-base' : '')}>&nbsp;</span>}
            {!embedded && !partner.phone && <span className={cn('text-sm', highlighted ? ' sm:text-base' : '')}>&nbsp;</span>}
            <div className={embedded ? 'pt-8 ' : ''}>
              <span className={cn('text-sm', highlighted ? ' sm:text-base' : '')}>{!location ? '' : '📍' + location}</span>
            </div>
            {embedded ? <></> : !partner.email ? <span className={cn('text-sm', highlighted ? ' sm:text-base' : '')}>&nbsp;</span> : (
              <a
                href={`mailto:${partner.email}`}
                className='block transition-colors duration-200 hover:font-bold'
                title={i18n.sendEmailTitle.replace('{partner}', partner.name)}
              >
                <span className={cn('text-sm', highlighted ? ' sm:text-base' : '')}>✉️ {partner.email}</span>
              </a>
            )}
            {embedded ? <></> : !partner.phone ? <span className={cn('text-sm', highlighted ? ' sm:text-base' : '')}>&nbsp;</span> : (
              <a
                href={`tel:${partner.phone}`}
                className='block transition-colors duration-200 hover:font-bold'
                title={i18n.callTitle.replace('{partner}', partner.name)}
              >
                <span className={cn('text-sm', highlighted ? ' sm:text-base' : '')}>📞 {partner.phone}</span>
              </a>
            )}
          </div>

          {/* Visit website button */}
          {!i18nValue(partner.website, langCode) ? <></> :
            <Link href={i18nValue(partner.website, langCode)}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={cn(embedded ? location ? 'mt-2 sm:mt-6' : 'mt-10' : 'mt-6', ' rounded-full border border-white/30 bg-white/20 px-6 py-2', highlighted ? ' sm:py-4' : '',
                                ' text-sm font-medium backdrop-blur-sm transition-all duration-300 hover:bg-white/30')}>
              {i18n.visitSite} →
            </Link>
          }
        </div>
      </Card>
    </div>
  )
}
